import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import TournamentBracket from '../components/TournamentBracket';
import './TournamentView.css';

/* ─────────────────────────────────────────────────────────────
   TOURNAMENT VIEW PAGE
   Handles both Single Elimination (bracket) and Round Robin
   (standings table + round-by-round match list).
   ───────────────────────────────────────────────────────────── */

/* ── Round Robin helpers (module-level pure functions) ───────── */

function computeRRStandings(players, matches) {
  const stats = {};
  players.forEach(p => {
    stats[p.player_id] = {
      playerId: p.player_id,
      username: p.username,
      elo_rating: p.elo_rating,
      w: 0, l: 0, pts: 0,
    };
  });
  matches.forEach(m => {
    if (m.status !== 'completed' || !m.winner_id) return;
    const loserId = m.winner_id === m.player1_id ? m.player2_id : m.player1_id;
    if (stats[m.winner_id]) { stats[m.winner_id].w += 1; stats[m.winner_id].pts += 1; }
    if (stats[loserId])     { stats[loserId].l += 1; }
  });
  return Object.values(stats).sort((a, b) => b.pts - a.pts || b.w - a.w);
}

function resolveRRChampion(standings, matches) {
  if (!standings.length) return null;
  const topPts = standings[0].pts;
  const tied   = standings.filter(s => s.pts === topPts);
  if (tied.length === 1) return [tied[0]];

  // Head-to-head among tied players
  const tiedIds = new Set(tied.map(s => s.playerId));
  const h2h = {};
  tied.forEach(s => { h2h[s.playerId] = 0; });
  matches.forEach(m => {
    if (m.status !== 'completed' || !m.winner_id) return;
    if (tiedIds.has(m.player1_id) && tiedIds.has(m.player2_id)) {
      h2h[m.winner_id] = (h2h[m.winner_id] || 0) + 1;
    }
  });
  const maxH2H     = Math.max(...Object.values(h2h));
  const h2hWinners = tied.filter(s => h2h[s.playerId] === maxH2H);
  return h2hWinners;
}

function groupByRound(matches) {
  return matches.reduce((acc, m) => {
    if (!acc[m.round]) acc[m.round] = [];
    acc[m.round].push(m);
    return acc;
  }, {});
}

function computeRRStandingsDoubles(teams, matches) {
  const stats = {};
  teams.forEach(t => {
    stats[t.id] = {
      teamId: t.id,
      label: `${t.player1?.username ?? '?'} & ${t.player2?.username ?? '?'}`,
      w: 0, l: 0, pts: 0,
    };
  });
  matches.forEach(m => {
    if (m.status !== 'completed' || !m.winner_team_id) return;
    const loserId = m.winner_team_id === m.team1_id ? m.team2_id : m.team1_id;
    if (stats[m.winner_team_id]) { stats[m.winner_team_id].w += 1; stats[m.winner_team_id].pts += 1; }
    if (stats[loserId])           { stats[loserId].l += 1; }
  });
  return Object.values(stats).sort((a, b) => b.pts - a.pts || b.w - a.w);
}

function resolveRRChampionDoubles(standings, matches) {
  if (!standings.length) return null;
  const topPts = standings[0].pts;
  const tied   = standings.filter(s => s.pts === topPts);
  if (tied.length === 1) return [tied[0]];
  const tiedIds = new Set(tied.map(s => s.teamId));
  const h2h = {};
  tied.forEach(s => { h2h[s.teamId] = 0; });
  matches.forEach(m => {
    if (m.status !== 'completed' || !m.winner_team_id) return;
    if (tiedIds.has(m.team1_id) && tiedIds.has(m.team2_id)) {
      h2h[m.winner_team_id] = (h2h[m.winner_team_id] || 0) + 1;
    }
  });
  const maxH2H     = Math.max(...Object.values(h2h));
  const h2hWinners = tied.filter(s => h2h[s.teamId] === maxH2H);
  return h2hWinners;
}

/* ── Component ───────────────────────────────────────────────── */
function TournamentView() {
  const { id }   = useParams();
  const { user } = useAuth();

  const [tournament,  setTournament]  = useState(null);
  const [players,     setPlayers]     = useState([]);
  const [matches,     setMatches]     = useState([]);
  const [teams,       setTeams]       = useState([]);   // doubles: [{id, seed, player1, player2}]
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);

  // Score submission modal
  const [activeMatch,  setActiveMatch]  = useState(null);
  const [winnerId,     setWinnerId]     = useState('');
  const [winnerScore,  setWinnerScore]  = useState('');
  const [loserScore,   setLoserScore]   = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  const loadTournament = useCallback(async () => {
    const { data: t } = await supabase
      .from('tournaments')
      .select('*')
      .eq('id', id)
      .single();

    if (!t) { setNotFound(true); setLoading(false); return; }
    setTournament(t);

    const { data: tm } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', id)
      .order('round')
      .order('match_number');

    if (t.is_doubles) {
      // ── Doubles: load teams ───────────────────────────────
      const { data: tt } = await supabase
        .from('tournament_teams')
        .select('id, seed, player1:profiles!player1_id(id, username, doubles_elo), player2:profiles!player2_id(id, username, doubles_elo)')
        .eq('tournament_id', id)
        .order('seed');

      const loadedTeams = tt || [];
      setTeams(loadedTeams);
      setPlayers([]);

      const tMap = {};
      loadedTeams.forEach(t2 => { tMap[t2.id] = t2; });

      setMatches((tm || []).map(m => ({
        ...m,
        team1: m.team1_id ? tMap[m.team1_id] || null : null,
        team2: m.team2_id ? tMap[m.team2_id] || null : null,
      })));
    } else {
      // ── Singles: load players ─────────────────────────────
      const { data: tp } = await supabase
        .from('tournament_players')
        .select('player_id, seed')
        .eq('tournament_id', id)
        .order('seed');

      const playerIds = (tp || []).map(r => r.player_id);
      let profileMap = {};
      if (playerIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, elo_rating')
          .in('id', playerIds);
        (profiles || []).forEach(p => { profileMap[p.id] = p; });
      }

      setPlayers((tp || []).map(r => ({ ...r, ...profileMap[r.player_id] })));
      setTeams([]);

      setMatches((tm || []).map(m => ({
        ...m,
        player1: m.player1_id ? profileMap[m.player1_id] || null : null,
        player2: m.player2_id ? profileMap[m.player2_id] || null : null,
      })));
    }

    setLoading(false);
  }, [id]);

  useEffect(() => { loadTournament(); }, [loadTournament]);

  // ── Score submission ────────────────────────────────────────
  function openSubmitModal(match) {
    setActiveMatch(match);
    setWinnerId('');
    setWinnerScore('');
    setLoserScore('');
    setSubmitError('');
  }

  function closeModal() { setActiveMatch(null); }

  function getWinnerScore(fmt) {
    return { bo1: 1, bo3: 2, bo5: 3, bo7: 4 }[fmt] ?? 1;
  }

  function getMaxLoserScore(fmt) {
    return { bo1: 0, bo3: 1, bo5: 2, bo7: 3 }[fmt] ?? 0;
  }

  async function handleSubmitScore() {
    if (!winnerId) { setSubmitError('Please select a winner.'); return; }

    const isBO1 = tournament.format === 'bo1';
    let ws, ls;

    if (isBO1) {
      ws = 1;
      ls = 0;
    } else {
      ws = parseInt(winnerScore, 10);
      ls = parseInt(loserScore,  10);
      const expectedWs = getWinnerScore(tournament.format);
      const maxLs      = getMaxLoserScore(tournament.format);

      if (isNaN(ws) || ws !== expectedWs) {
        setSubmitError(`Winner score must be ${expectedWs} for ${tournament.format.toUpperCase()}.`);
        return;
      }
      if (isNaN(ls) || ls < 0 || ls > maxLs) {
        setSubmitError(`Loser score must be between 0 and ${maxLs}.`);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError('');

    // Use the correct RPC depending on tournament type and singles/doubles
    let rpcName, rpcParams;
    if (tournament.is_doubles) {
      rpcName = isRR ? 'submit_doubles_rr_match' : 'submit_doubles_tournament_match';
      rpcParams = { p_tournament_match_id: activeMatch.id, p_winner_team_id: winnerId, p_winner_score: ws, p_loser_score: ls };
    } else {
      rpcName = isRR ? 'submit_rr_match' : 'submit_tournament_match';
      rpcParams = { p_tournament_match_id: activeMatch.id, p_winner_id: winnerId, p_winner_score: ws, p_loser_score: ls };
    }

    const { data, error } = await supabase.rpc(rpcName, rpcParams);

    setSubmitting(false);

    if (error || data?.error) {
      setSubmitError(error?.message || data?.error || 'Failed to submit score.');
      return;
    }

    closeModal();
    loadTournament();
    if (tournament.counts_towards_elo) {
      window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
    }
  }

  // ── Derived values ──────────────────────────────────────────
  const isCreator  = user && tournament && tournament.creator_id === user.id;
  const isRR       = tournament?.tournament_type === 'round_robin';
  const isDoubles  = tournament?.is_doubles ?? false;

  // Build teamMap for doubles bracket rendering
  const teamMap = {};
  teams.forEach(t => { teamMap[t.id] = t; });

  // SE champion
  const finalMatch = !isRR ? matches.find(m => {
    const totalRounds = Math.log2(tournament?.player_count || 8);
    return m.round === totalRounds && m.match_number === 1;
  }) : null;
  const seChampion = !isDoubles && finalMatch?.winner_id
    ? players.find(p => p.player_id === finalMatch.winner_id)
    : null;
  const seChampionTeam = isDoubles && finalMatch?.winner_team_id
    ? teamMap[finalMatch.winner_team_id]
    : null;

  // RR derived
  const rrStandings       = (isRR && !isDoubles) ? computeRRStandings(players, matches) : [];
  const rrStandingsDoubles = (isRR && isDoubles)  ? computeRRStandingsDoubles(teams, matches) : [];
  const rrChampion = (isRR && !isDoubles && tournament?.status === 'completed')
    ? resolveRRChampion(rrStandings, matches)
    : null;
  const rrChampionDoubles = (isRR && isDoubles && tournament?.status === 'completed')
    ? resolveRRChampionDoubles(rrStandingsDoubles, matches)
    : null;
  const rrRounds = isRR ? groupByRound(matches) : {};

  const formatLabel = { bo1: 'Best of 1', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };

  if (loading) {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <div className="tv-loading">
            <span>★</span><span>★</span><span>★</span>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <p className="tv-not-found">Tournament not found.</p>
          <Link to="/tournaments" className="tv-back-link">← Back to Tournaments</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="tv-page">
      <div className="tv-container">

        {/* Header */}
        <div className="tv-header">
          <Link to="/tournaments" className="tv-back">← Tournaments</Link>
          <div className="tv-header-main">
            <div>
              <h1 className="tv-title">{tournament.name}</h1>
              <div className="tv-meta">
                <span className={`trn-badge ${tournament.status === 'active' ? 'trn-badge--active' : 'trn-badge--done'}`}>
                  {tournament.status === 'active' ? 'In Progress' : 'Completed'}
                </span>
                <span className="tv-meta-sep">·</span>
                <span>{tournament.player_count} {isDoubles ? 'teams' : 'players'}</span>
                <span className="tv-meta-sep">·</span>
                <span>{formatLabel[tournament.format]}</span>
                <span className="tv-meta-sep">·</span>
                <span>{tournament.counts_towards_elo ? 'Ranked' : 'Unranked'}</span>
                <span className="tv-meta-sep">·</span>
                <span>{isRR ? 'Round Robin' : 'Single Elim'}</span>
                {isDoubles && <><span className="tv-meta-sep">·</span><span className="trn-badge trn-badge--2v2">2v2</span></>}
              </div>
            </div>
          </div>
        </div>

        {/* SE champion banners */}
        {!isRR && seChampion && (
          <div className="tv-champion-banner">
            <span className="tv-champion-trophy">🏆</span>
            <div>
              <p className="tv-champion-label">Tournament Champion</p>
              <p className="tv-champion-name">{seChampion.username}</p>
            </div>
          </div>
        )}
        {!isRR && seChampionTeam && (
          <div className="tv-champion-banner">
            <span className="tv-champion-trophy">🏆</span>
            <div>
              <p className="tv-champion-label">Tournament Champions</p>
              <p className="tv-champion-name">
                {seChampionTeam.player1?.username} &amp; {seChampionTeam.player2?.username}
              </p>
            </div>
          </div>
        )}

        {/* RR champion banners */}
        {isRR && rrChampion && (
          <div className="tv-champion-banner">
            <span className="tv-champion-trophy">🏆</span>
            <div>
              <p className="tv-champion-label">
                {rrChampion.length > 1 ? 'Co-Champions' : 'Tournament Champion'}
              </p>
              <p className="tv-champion-name">
                {rrChampion.map(c => c.username).join(' & ')}
              </p>
            </div>
          </div>
        )}
        {isRR && rrChampionDoubles && (
          <div className="tv-champion-banner">
            <span className="tv-champion-trophy">🏆</span>
            <div>
              <p className="tv-champion-label">
                {rrChampionDoubles.length > 1 ? 'Co-Champions' : 'Tournament Champions'}
              </p>
              <p className="tv-champion-name">
                {rrChampionDoubles.map(c => c.label).join(' / ')}
              </p>
            </div>
          </div>
        )}

        {/* ── Single Elimination: bracket + seeding ─────────── */}
        {!isRR && (
          <>
            <TournamentBracket
              matches={matches}
              playerCount={tournament.player_count}
              isCreator={isCreator && tournament.status === 'active'}
              onSubmit={openSubmitModal}
              isDoubles={isDoubles}
              teamMap={teamMap}
            />

            <div className="tv-seeding-info">
              <p className="tv-seeding-label">Seeding</p>
              <div className="tv-seeds">
                {isDoubles ? teams.map(t => {
                  const avgElo = Math.round(((t.player1?.doubles_elo ?? 1200) + (t.player2?.doubles_elo ?? 1200)) / 2);
                  return (
                    <div key={t.id} className="tv-seed-row">
                      <span className="tv-seed-num">#{t.seed}</span>
                      <div className="tv-seed-avatar">{t.player1?.username?.[0]?.toUpperCase()}</div>
                      <span className="tv-seed-name">{t.player1?.username} &amp; {t.player2?.username}</span>
                      <span className="tv-seed-elo">{avgElo}</span>
                    </div>
                  );
                }) : players.map(p => (
                  <div key={p.player_id} className="tv-seed-row">
                    <span className="tv-seed-num">#{p.seed}</span>
                    <div className="tv-seed-avatar">{p.username?.[0]?.toUpperCase()}</div>
                    <span className="tv-seed-name">{p.username}</span>
                    <span className="tv-seed-elo">{p.elo_rating}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Round Robin: standings + rounds ───────────────── */}
        {isRR && (
          <>
            {/* Standings table */}
            <div className="tv-rr-standings">
              <p className="tv-rr-section-label">Standings</p>
              <table className="tv-rr-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>{isDoubles ? 'Team' : 'Player'}</th>
                    <th>W</th>
                    <th>L</th>
                    <th>Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {(isDoubles ? rrStandingsDoubles : rrStandings).map((s, i) => (
                    <tr key={isDoubles ? s.teamId : s.playerId}>
                      <td className="tv-rr-rank">#{i + 1}</td>
                      <td className="tv-rr-player">
                        <div className="tv-seed-avatar">
                          {isDoubles ? s.label[0].toUpperCase() : s.username[0].toUpperCase()}
                        </div>
                        <span>{isDoubles ? s.label : s.username}</span>
                      </td>
                      <td>{s.w}</td>
                      <td>{s.l}</td>
                      <td className="tv-rr-pts">{s.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Round-by-round match list */}
            {Object.keys(rrRounds)
              .sort((a, b) => Number(a) - Number(b))
              .map(round => (
                <div key={round} className="tv-rr-round">
                  <p className="tv-rr-section-label">Round {round}</p>
                  <div className="tv-rr-match-list">
                    {rrRounds[round].map(match => {
                      const done = match.status === 'completed';
                      if (isDoubles) {
                        const t1    = match.team1;
                        const t2    = match.team2;
                        const t1Lbl = t1 ? `${t1.player1?.username} & ${t1.player2?.username}` : 'TBD';
                        const t2Lbl = t2 ? `${t2.player1?.username} & ${t2.player2?.username}` : 'TBD';
                        return (
                          <div key={match.id} className="tv-rr-match-row">
                            <span className="tv-rr-matchup">
                              <span className={done && match.winner_team_id === match.team1_id ? 'tv-rr-winner' : ''}>{t1Lbl}</span>
                              <span className="tv-rr-vs">{done ? `${match.player1_score}–${match.player2_score}` : 'vs'}</span>
                              <span className={done && match.winner_team_id === match.team2_id ? 'tv-rr-winner' : ''}>{t2Lbl}</span>
                            </span>
                            {isCreator && !done && tournament.status === 'active' && t1 && t2 && (
                              <button className="tv-rr-enter-btn" onClick={() => openSubmitModal(match)}>Enter Score</button>
                            )}
                          </div>
                        );
                      }
                      const p1 = match.player1;
                      const p2 = match.player2;
                      return (
                        <div key={match.id} className="tv-rr-match-row">
                          <span className="tv-rr-matchup">
                            <span className={match.winner_id === match.player1_id ? 'tv-rr-winner' : ''}>{p1?.username ?? 'TBD'}</span>
                            <span className="tv-rr-vs">{done ? `${match.player1_score}–${match.player2_score}` : 'vs'}</span>
                            <span className={match.winner_id === match.player2_id ? 'tv-rr-winner' : ''}>{p2?.username ?? 'TBD'}</span>
                          </span>
                          {isCreator && !done && tournament.status === 'active' && p1 && p2 && (
                            <button className="tv-rr-enter-btn" onClick={() => openSubmitModal(match)}>Enter Score</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </>
        )}

      </div>

      {/* Score submission modal — shared by both tournament types */}
      {activeMatch && (
        <div className="tv-modal-overlay" onClick={closeModal}>
          <div className="tv-modal" onClick={e => e.stopPropagation()}>
            <div className="tv-modal-header">
              <h2 className="tv-modal-title">Submit Score</h2>
              <button className="tv-modal-close" onClick={closeModal}>✕</button>
            </div>

            <p className="tv-modal-sub">
              {formatLabel[tournament.format]} · {tournament.counts_towards_elo ? 'Ranked' : 'Unranked'}
            </p>

            <div className="tv-modal-field">
              <label className="tv-modal-label">Winner</label>
              <div className="tv-modal-players">
                {isDoubles ? (
                  // Doubles: show team buttons
                  [
                    { id: activeMatch.team1_id, team: activeMatch.team1 },
                    { id: activeMatch.team2_id, team: activeMatch.team2 },
                  ].map(({ id, team }) => team && (
                    <button
                      key={id}
                      className={`tv-player-btn ${winnerId === id ? 'active' : ''}`}
                      onClick={() => setWinnerId(id)}
                    >
                      <div className="tv-player-btn-avatar">{team.player1?.username?.[0]?.toUpperCase()}</div>
                      <span>{team.player1?.username} &amp; {team.player2?.username}</span>
                      <span className="tv-player-btn-elo">
                        {Math.round(((team.player1?.doubles_elo ?? 1200) + (team.player2?.doubles_elo ?? 1200)) / 2)}
                      </span>
                    </button>
                  ))
                ) : (
                  // Singles: show player buttons
                  [activeMatch.player1, activeMatch.player2].map(p => p && (
                    <button
                      key={p.id}
                      className={`tv-player-btn ${winnerId === p.id ? 'active' : ''}`}
                      onClick={() => setWinnerId(p.id)}
                    >
                      <div className="tv-player-btn-avatar">{p.username[0].toUpperCase()}</div>
                      <span>{p.username}</span>
                      <span className="tv-player-btn-elo">{p.elo_rating}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {winnerId && tournament.format !== 'bo1' && (
              <div className="tv-modal-scores">
                <div className="tv-modal-field">
                  <label className="tv-modal-label">
                    {isDoubles
                      ? (() => { const t = teamMap[winnerId]; return t ? `${t.player1?.username} & ${t.player2?.username}` : 'Winner'; })()
                      : [activeMatch.player1, activeMatch.player2].find(p => p?.id === winnerId)?.username
                    } (winner)
                  </label>
                  <input
                    className="tv-modal-score-input"
                    type="number"
                    min="0"
                    max={getWinnerScore(tournament.format)}
                    placeholder={`${getWinnerScore(tournament.format)}`}
                    value={winnerScore}
                    onChange={e => setWinnerScore(e.target.value)}
                  />
                </div>
                <div className="tv-modal-field">
                  <label className="tv-modal-label">
                    {isDoubles
                      ? (() => {
                          const loserId = winnerId === activeMatch.team1_id ? activeMatch.team2_id : activeMatch.team1_id;
                          const t = teamMap[loserId];
                          return t ? `${t.player1?.username} & ${t.player2?.username}` : 'Loser';
                        })()
                      : [activeMatch.player1, activeMatch.player2].find(p => p?.id !== winnerId && p)?.username
                    } (loser)
                  </label>
                  <input
                    className="tv-modal-score-input"
                    type="number"
                    min="0"
                    max={getMaxLoserScore(tournament.format)}
                    placeholder="0"
                    value={loserScore}
                    onChange={e => setLoserScore(e.target.value)}
                  />
                </div>
              </div>
            )}

            {submitError && <p className="tv-modal-error">{submitError}</p>}

            <div className="tv-modal-actions">
              <button className="tv-modal-cancel" onClick={closeModal}>Cancel</button>
              <button
                className="tv-modal-submit"
                onClick={handleSubmitScore}
                disabled={submitting || !winnerId}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentView;
